import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, ChevronDown } from "lucide-react";

/**
 * FinishedProductPicker
 * - Searchable dropdown for finished-product master.
 * - Props: value (product id), onPick(product), placeholder, testId, disabled
 */
export default function FinishedProductPicker({ value, onPick, placeholder = "Pick finished product…", testId = "fp-picker", disabled, fallbackLabel }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/org/finished-products").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const current = useMemo(() => items.find((p) => p.id === value), [items, value]);
  const filtered = items.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) || (p.code || "").toLowerCase().includes(s) || (p.design_name || "").toLowerCase().includes(s) || (p.size || "").toLowerCase().includes(s);
  });

  const triggerLabel = current
    ? `${current.code} · ${current.name}${current.size ? ` · ${current.size}` : ""}`
    : (fallbackLabel || null);

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className="w-full flex items-center justify-between border border-input rounded-md px-3 h-10 text-sm bg-background hover:bg-accent/40 disabled:opacity-60"
        data-testid={`${testId}-trigger`}
      >
        <span className={triggerLabel ? "" : "text-muted-foreground"}>
          {triggerLabel || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 opacity-60 flex-shrink-0" />
      </button>
      {open ? (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-xl max-h-72 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 border-b border-border px-2 py-1">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, code, design, size…"
              className="border-0 h-8 focus-visible:ring-0"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto max-h-60">
            {filtered.length === 0 ? (
              <div className="p-3 text-center text-xs text-muted-foreground">No products found. Add products in <span className="font-mono">Stock & WH → Finished products</span>.</div>
            ) : null}
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onPick(p); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center justify-between ${value === p.id ? "bg-accent/60" : ""}`}
                data-testid={`${testId}-option-${p.code}`}
              >
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{p.code}{p.size ? ` · ${p.size}` : ""}{p.design_name ? ` · ${p.design_name}` : ""}</div>
                </div>
                <div className="text-xs font-mono text-muted-foreground">₹{Number(p.sale_price || 0).toLocaleString()}</div>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
